import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WorkflowData } from "./WorkflowEditor";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Clock, Target, CheckCircle2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface WorkflowPreviewModalProps {
  open: boolean;
  onClose: () => void;
  workflow: WorkflowData;
}

export function WorkflowPreviewModal({ open, onClose, workflow }: WorkflowPreviewModalProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Workflow Preview</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-start justify-between mb-2">
              <h2 className="text-2xl font-bold">{workflow.title || "Untitled Workflow"}</h2>
              <Badge variant={workflow.is_active ? "default" : "secondary"}>
                {workflow.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
            <p className="text-muted-foreground">{workflow.description}</p>
            
            <div className="flex gap-4 mt-4">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4" />
                {workflow.estimated_time}
              </div>
              <Badge variant="outline">{workflow.difficulty}</Badge>
              <Badge variant="outline">{workflow.category.replace('_', ' ')}</Badge>
            </div>
          </div>

          {/* Phases */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Workflow Phases ({workflow.phases.length})</h3>
            <div className="space-y-3">
              {workflow.phases.map((phase: any, index: number) => (
                <Card key={phase.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium mb-1">{phase.name || "Untitled Phase"}</h4>
                      <p className="text-sm text-muted-foreground mb-2">{phase.description}</p>
                      
                      {phase.estimated_time && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                          <Clock className="h-3 w-3" />
                          {phase.estimated_time}
                        </div>
                      )}

                      {phase.tasks && phase.tasks.length > 0 && (
                        <div className="space-y-1 mt-2">
                          {phase.tasks.map((task: string, taskIndex: number) => (
                            <div key={taskIndex} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-3 w-3 text-muted-foreground" />
                              {task}
                            </div>
                          ))}
                        </div>
                      )}

                      {phase.required_documents && phase.required_documents.length > 0 && (
                        <div className="mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {phase.required_documents.length} document(s)
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Success Criteria */}
          {workflow.success_criteria.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Success Criteria</h3>
              <ul className="space-y-2">
                {workflow.success_criteria.map((criteria, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    <span className="text-sm">{criteria}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Guidance Notes */}
          {workflow.guidance_notes && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Guidance</h3>
              <Card className="p-4">
                <p className="text-sm whitespace-pre-wrap">{workflow.guidance_notes}</p>
              </Card>
            </div>
          )}

          {/* Tier Restrictions */}
          {workflow.tier_restrictions.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Available For</h3>
              <div className="flex gap-2">
                {workflow.tier_restrictions.map(tier => (
                  <Badge key={tier} variant="outline" className="capitalize">
                    {tier.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}